/**
 * Fires the server-side comment scoring pass after a comment is posted or
 * edited.
 *
 * Fire-and-forget (`comment.tsx` wraps it in `fireAndForget`): a post or edit
 * succeeds independently of scoring, so there is no UI state to wait on and a
 * failure just routes to Sentry. `comments.score` no-ops server-side unless
 * `llm-ranking` is enabled, so this calls it unconditionally without checking
 * client config. On completion it bumps a session-scoped counter so the
 * E2E-only scoring-complete marker in `llm-ranking.tsx` can tell when the Nth
 * post's scoring has finished.
 *
 * This is the sole server behavior driven by a client follow-up call; keep it
 * that way. New post-write server work belongs behind a server-side
 * deferred-work mechanism, not another client trigger.
 */
import type { Datastore } from 'util/datastore';

import { siloParamsOf } from './silo-params';

// Counts finished scoring calls this session. Read only by the E2E
// scoring-complete marker in `feature/ranking/llm-ranking.tsx`.
export const SCORING_DONE_KEY = ['comment-scoring', 'done'];

export async function scoreCommentAsync(datastore: Datastore, { commentKey }: { commentKey: string }): Promise<void> {
    const { siloKey, locale } = siloParamsOf(datastore);
    const spaceKey = datastore.getInstanceKey();
    try {
        await datastore.getApiClient().comments.score({ siloKey, locale, spaceKey, commentKey });
    } finally {
        const done = (datastore.getSessionData(SCORING_DONE_KEY) as number | null) ?? 0;
        datastore.setSessionData(SCORING_DONE_KEY, done + 1);
    }
}
